import React from 'react';
import { createPunchItem } from '../../../lib/api';
import {
  CAT_NOTE, CAT_TITLE, CATEGORIES, catRule, isQaCloseout,
} from '../../../lib/handoverView';
import { HoDialog } from './HoBits';

/**
 * H-FE-1 B1 — "Add punch item" (prototype `act('raise')`), raised ON an
 * inspection by either of its two companies (the Executing Party alone on a
 * QA Closeout). The canonical creator (`createPunchItem`), the same POST the
 * whole app uses.
 *
 * The company that ACCEPTS the fix is never chosen: the server derives it from
 * the inspection (the receiving company; the Executing Party on a closeout).
 * The correcting company is pre-filled with the handing-over company.
 *
 * No Photo field: the server has no raise-photo act — a photo attached while the
 * item is open is RECTIFICATION evidence of round 1 (tmp/h-fe1/be-gaps.md §7).
 * "Reason" appears only when the chosen asset is already QA Complete.
 */
export default function RaisePunchDialog({
  hi, assets = [], preAsset = null, onClose, onRaised,
}) {
  const qc = isQaCloseout(hi);
  const fromId = hi?.handover_from_project_company_id;
  const toId = hi?.handover_to_project_company_id;
  const companies = qc ? [[fromId, hi?.handover_from_project_company_name]]
    : [[fromId, hi?.handover_from_project_company_name], [toId, hi?.handover_to_project_company_name]];
  const qaDone = (eid) => !!(assets.find((a) => a.element_id === eid)?.qa_complete);
  const completed = hi?.status === 'completed';
  const docId = hi?.document_id || '';

  const fields = [
    {
      k: 'asset', label: 'Asset', type: 'select', req: true,
      options: assets.map((a) => ({
        v: a.element_id,
        l: `${a.asset_name || a.asset_number || a.element_id}${a.qa_complete ? ' — QA Complete' : ''}`,
        on: a.element_id === preAsset,
      })),
    },
    {
      k: 'cat', label: 'Category', type: 'radios', req: true, hint: CAT_NOTE,
      options: CATEGORIES.map((c) => ({ v: c, l: `${c} — ${CAT_TITLE[c]}`, s: catRule(c, qc), on: c === 'A' })),
    },
    {
      k: 'desc', label: 'Description', type: 'textarea', req: true,
      placeholder: 'e.g., Pit lid not seated; 12 mm proud on the east edge',
    },
    {
      k: 'resp', label: 'Correcting company', type: 'select', req: true,
      options: companies.filter(([id]) => id).map(([id, name]) => ({
        v: id, l: `${name || id}${id === fromId ? ' (pre-filled: the handing-over company)' : ''}`, on: id === fromId,
      })),
    },
    {
      k: 'late', label: 'Reason — this asset is already QA Complete', type: 'textarea', req: true,
      hint: 'A late item after QA Complete asks for a reason; the stamp stays and the item is tracked as late.',
      placeholder: 'Why this item is raised after QA Complete',
      show: (v) => qaDone(v.asset),
    },
  ];

  const submit = async (v) => {
    const created = await createPunchItem({
      inspection_id: hi.inspection_id || hi.id,
      element_id: v.asset,
      description: v.desc.trim(),
      category: v.cat,
      responsible_project_company_id: v.resp || null,
      reason: qaDone(v.asset) ? v.late.trim() : null,
    });
    onRaised(created);
  };

  return (
    <HoDialog
      size="mid"
      title="Add punch item"
      sub={`${docId}${completed ? ' · the inspection is already completed: this item will be flagged as late' : ''}`}
      fields={fields}
      okLabel="Add item"
      onSubmit={submit}
      onClose={onClose}
      testId="raise-dialog"
    />
  );
}
